import type {
  Batch,
  DetailedProposal,
  ListProposalsParams,
  ProposalStatusResponse,
  ProposalVersionSummary,
  ReplacementCandidatesResponse,
  ResetProposalConstraintsResponse,
  SlimProposal,
  StartProposalRequest,
  StartProposalResponse,
  UpdateProposalAccessResponse,
} from '@moment/horizon-data-access/proposal'
import type {
  CreateProposalOrderRequest,
  ListProposalStatusesRequest,
  ListProposalStatusesResponse,
  ProposalAllocationParam,
  ProposalIdParam,
  ProposalOrderParam,
  ReplacementCandidatesParam,
  UpdateOrderAllocationRequest,
  UpdateProposalAccessRequest,
  UpdateProposalLotRequest,
  UpdateProposalLotResponse,
  UpdateProposalOrderRequest,
} from '@/legacy-api/proposals-slice-types'
import type {
  BulkCancelProposalsRequest,
  ProposalCancelSummary,
} from '@/types/api/bulk-cancel-proposals'
import type { OrderRequestMetadata } from '@/types/api/order'
import type {
  CreateSyntheticTargetParams,
  SyntheticProposalRequest,
  SyntheticTargetResponse,
} from '@/types/api/synthetic-target'

import { TagTypes } from '@/types/api/tag-types'

import { baseApiSlice } from './base-api-slice'

/** The tag of one proposal's live read, to refetch it without touching the rest. */
export function proposalTag(proposalId: string) {
  return { type: TagTypes.Proposals, id: proposalId } as const
}

const proposalSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Batch detail with computed status→count summary. Polled (not tag-driven)
    // by the batch-proposal tracker to follow up on async batch creation.
    getBatch: builder.query<Batch, { batch_id: string }>({
      query: ({ batch_id }) => ({
        url: `/proposal/batch/${batch_id}/`,
        method: 'GET',
      }),
    }),
    startProposal: builder.mutation<
      StartProposalResponse,
      StartProposalRequest
    >({
      query: (body) => ({
        url: `/proposal/start/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // https://docs.withmoment.com/reference/listproposals
    listAllProposals: builder.query<SlimProposal[], ListProposalsParams>({
      query: ({ status } = {}) => ({
        url: `/proposal/`,
        method: 'GET',
        params: {
          status,
        },
      }),
      providesTags: [TagTypes.Proposals],
    }),
    // https://docs.withmoment.com/reference/getproposal
    getProposal: builder.query<DetailedProposal, ProposalIdParam>({
      query: ({ proposal_id }) => ({
        url: `/proposal/${proposal_id}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { proposal_id }) => [
        TagTypes.Proposals,
        proposalTag(proposal_id),
      ],
    }),
    getProposalStatus: builder.query<ProposalStatusResponse, ProposalIdParam>({
      query: ({ proposal_id }) => ({
        url: `/proposal/${proposal_id}/status/`,
        method: 'GET',
      }),
    }),
    // Handler: portfolios/propman/internal/proposals (listProposalStatuses). Polled by the batch-proposal tracker.
    listProposalStatuses: builder.query<
      ListProposalStatusesResponse,
      ListProposalStatusesRequest
    >({
      query: (body) => ({
        url: `/proposal/statuses/`,
        method: 'POST',
        body,
      }),
    }),
    // https://docs.withmoment.com/reference/cancelproposal
    cancelProposal: builder.mutation<DetailedProposal, ProposalIdParam>({
      query: ({ proposal_id }) => ({
        url: `/proposal/${proposal_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // Handler: portfolios/propman/internal/proposals/service_bulk_cancel_proposals.go
    bulkCancelProposals: builder.mutation<
      ProposalCancelSummary,
      BulkCancelProposalsRequest
    >({
      query: (body) => ({
        url: `/proposal/bulk-cancel/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // https://docs.withmoment.com/reference/executeproposal
    submitProposal: builder.mutation<
      DetailedProposal,
      {
        proposal_id: string
        metadata?: OrderRequestMetadata
        bypass_auto_execution?: boolean
      }
    >({
      query: ({ proposal_id, metadata, bypass_auto_execution }) => ({
        url: `/proposal/${proposal_id}/execute/`,
        method: 'POST',
        body: {
          ...(metadata ? { metadata } : {}),
          ...(bypass_auto_execution ? { bypass_auto_execution } : {}),
        },
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // Clear every model's constraints and re-apply the global defaults. Handler:
    // portfolios/propman/internal/proposals/service_reset_proposal_constraints.go
    resetProposalConstraints: builder.mutation<
      ResetProposalConstraintsResponse,
      ProposalIdParam
    >({
      query: ({ proposal_id }) => ({
        url: `/proposal/${proposal_id}/constraints/reset/`,
        method: 'POST',
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // https://docs.withmoment.com/reference/cancelorders
    cancelOutstandingProposals: builder.mutation<
      DetailedProposal,
      ProposalIdParam
    >({
      query: ({ proposal_id }) => ({
        url: `/proposal/${proposal_id}/order/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // Create a new proposal order
    // POST /proposal/{proposal_id}/order/
    createProposalOrder: builder.mutation<
      DetailedProposal,
      CreateProposalOrderRequest
    >({
      query: ({
        proposal_id,
        instrument_id,
        side,
        limit_price,
        quantity,
        allocations,
      }) => ({
        url: `/proposal/${proposal_id}/order/`,
        method: 'POST',
        body: {
          instrument_id,
          side,
          limit_price,
          quantity,
          allocations,
        },
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // https://docs.withmoment.com/reference/updateorder
    updateProposalOrder: builder.mutation<
      DetailedProposal,
      UpdateProposalOrderRequest
    >({
      query: ({ proposal_id, proposal_order_id, allocations, ...fields }) => ({
        url: `/proposal/${proposal_id}/order/${proposal_order_id}/`,
        method: 'PATCH',
        body: {
          ...fields,
          ...(allocations?.length ? { allocations } : {}),
        },
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // https://docs.withmoment.com/reference/deleteorder
    deleteProposalOrder: builder.mutation<DetailedProposal, ProposalOrderParam>(
      {
        query: ({ proposal_id, proposal_order_id }) => ({
          url: `/proposal/${proposal_id}/order/${proposal_order_id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: [TagTypes.Proposals],
      },
    ),
    // https://docs.withmoment.com/reference/updateallocation
    updateProposalAllocation: builder.mutation<
      DetailedProposal,
      UpdateOrderAllocationRequest
    >({
      query: ({
        proposal_id,
        proposal_order_id,
        proposal_allocation_id,
        quantity,
        lock,
      }) => ({
        url: `/proposal/${proposal_id}/order/${proposal_order_id}/allocation/${proposal_allocation_id}/`,
        method: 'PATCH',
        body: { quantity, lock },
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // https://docs.withmoment.com/reference/deleteallocation-1
    deleteProposalAllocation: builder.mutation<
      DetailedProposal,
      ProposalAllocationParam
    >({
      query: ({ proposal_id, proposal_order_id, proposal_allocation_id }) => ({
        url: `/proposal/${proposal_id}/order/${proposal_order_id}/allocation/${proposal_allocation_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
    // Declarative lot-row edit: propman reconciles the row's order state
    // (create/update/delete/flip) from the absolute post-trade target and
    // returns only a version marker — the caller refetches the proposal
    // once, so no tag invalidation (it would fire a second redundant GET).
    // PATCH /proposal/{proposal_id}/lot/{moment_lot_id}/
    updateProposalLot: builder.mutation<
      UpdateProposalLotResponse,
      UpdateProposalLotRequest
    >({
      query: ({ proposal_id, moment_lot_id, ...body }) => ({
        url: `/proposal/${proposal_id}/lot/${moment_lot_id}/`,
        method: 'PATCH',
        body,
      }),
    }),
    // https://docs.withmoment.com/reference/createsynthetictarget
    createProposalFromPrompt: builder.mutation<
      SyntheticTargetResponse,
      CreateSyntheticTargetParams
    >({
      query: ({ prompt, investmentGuidelines, holdings }) => ({
        url: `/llm-models/synthetic-target/`,
        method: 'POST',
        body: { prompt, investmentGuidelines, holdings },
      }),
    }),
    // https://docs.withmoment.com/reference/createsyntheticproposal
    createProposalFromSyntheticTarget: builder.mutation<
      DetailedProposal,
      SyntheticProposalRequest
    >({
      query: (body) => {
        const sanitizedTargets = body.targets
          ? body.targets.map((target) => ({
              ...target,
              extra: {},
            }))
          : body.targets

        const sanitizedBody: SyntheticProposalRequest = {
          ...body,
          targets: sanitizedTargets,
        }

        return {
          url: `/proposal/synthetic/`,
          method: 'POST',
          body: sanitizedBody,
        }
      },
      invalidatesTags: [TagTypes.Proposals],
    }),
    // https://docs.withmoment.com/reference/getreplacementcandidates
    getReplacementCandidates: builder.query<
      ReplacementCandidatesResponse,
      ReplacementCandidatesParam
    >({
      query: ({ proposal_id, moment_order_id, limit }) => ({
        url: `/proposal/${proposal_id}/order/${moment_order_id}/replacement-candidates/`,
        method: 'GET',
        params: { limit },
      }),
      providesTags: [TagTypes.Proposals],
    }),

    updateProposalAccess: builder.mutation<
      UpdateProposalAccessResponse,
      UpdateProposalAccessRequest
    >({
      query: ({ proposal_id, users }) => ({
        url: `/proposal/${proposal_id}/access/`,
        method: 'PUT',
        body: { users },
      }),
    }),
    getProposalVersions: builder.query<
      ProposalVersionSummary[],
      ProposalIdParam
    >({
      query: ({ proposal_id }) => ({
        url: `/proposal/${proposal_id}/versions/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.Proposals],
    }),
    getProposalVersion: builder.query<
      DetailedProposal,
      { proposal_id: string; version: number }
    >({
      query: ({ proposal_id, version }) => ({
        url: `/proposal/${proposal_id}/versions/${version}/`,
        method: 'GET',
      }),
      // Per-version tag that no mutation invalidates, so expanded rows don't
      // refetch on edits. The versions LIST stays on Proposals.
      providesTags: (_result, _error, { proposal_id, version }) => [
        { type: TagTypes.ProposalVersion, id: `${proposal_id}:${version}` },
      ],
    }),
    restoreProposalVersion: builder.mutation<
      DetailedProposal,
      { proposal_id: string; version: number }
    >({
      query: ({ proposal_id, version }) => ({
        url: `/proposal/${proposal_id}/restore-version/${version}/`,
        method: 'POST',
      }),
      invalidatesTags: [TagTypes.Proposals],
    }),
  }),
})

export const {
  endpoints: {
    startProposal,
    cancelProposal,
    bulkCancelProposals,
    listAllProposals,
    getProposal,
    getProposalStatus,
    submitProposal,
    cancelOutstandingProposals,
    createProposalOrder,
    updateProposalOrder,
    deleteProposalOrder,
    updateProposalAllocation,
    deleteProposalAllocation,
    createProposalFromPrompt,
    getReplacementCandidates,
    createProposalFromSyntheticTarget,
    updateProposalAccess,
    getProposalVersions,
    getProposalVersion,
    restoreProposalVersion,
    resetProposalConstraints,
  },
  useGetBatchQuery,
  useListProposalStatusesQuery,
  useStartProposalMutation,
  useCancelProposalMutation,
  useBulkCancelProposalsMutation,
  useListAllProposalsQuery,

  useCancelOutstandingProposalsMutation,
  useCreateProposalOrderMutation,
  useUpdateProposalOrderMutation,
  useDeleteProposalOrderMutation,
  useUpdateProposalAllocationMutation,
  useDeleteProposalAllocationMutation,
  useUpdateProposalLotMutation,
  useGetProposalQuery,
  useGetProposalStatusQuery,
  useLazyGetProposalQuery,

  useGetReplacementCandidatesQuery,
  useCreateProposalFromSyntheticTargetMutation,
  useUpdateProposalAccessMutation,
  useGetProposalVersionsQuery,
  useGetProposalVersionQuery,
  useRestoreProposalVersionMutation,
  useResetProposalConstraintsMutation,
} = proposalSlice
